import * as Sentry from "@sentry/vue";
import { client as apollo } from "@/graphql/vue-apollo";
import createCustomerAccessTokenMutation from "@/graphql/mutations/CreateCustomerAccessToken.gql";
import customerAccessTokenCreateWithMultipass from "@/graphql/mutations/CreateCustomerAccessTokenWithMultipass.gql";
import createUserMutation from "@/graphql/mutations/CreateUser.gql";
import isEmpty from "lodash-es/isEmpty";
import add from "date-fns/add";
import customerQuery from "@/graphql/queries/Customer.gql";
import { registerEvent, loginEvent } from "@/helpers/events.js";

const state = {
  customerAccessToken: null,
  customer: {
    id: null,
    email: null,
    phone: null,
    birthday: null,
    loyalty: null,
    pendingPoints: null,
    approvedPoints: null,
    loyaltyTier: null,
    tags: null,
    wishlists: [],
  },
  multipassAccount: {
    url: null,
    token: null,
  },
};

const mutations = {
  setCustomerAccessToken(state, value) {
    state.customerAccessToken = value;
  },
  setCustomer(state, value) {
    state.customer = value;
  },
  setMultipassAccount(state, value) {
    state.multipassAccount = value;
  },
};

const getters = {
  isLoggedIn: (state) => {
    if (state.customerAccessToken) {
      return true;
    }

    return false;
  },
  hasPhoneNumber: (state) => {
    if (state.customer.phone) {
      return true;
    }

    return false;
  },
  hasBirthday: (state) => {
    if (state.customer.birthday) {
      return true;
    }

    return false;
  },
  wishlist: (state) => {
    return state.customer.wishlists[0]?.productVariants || null;
  },
  top2Wishlist: (state) => {
    return state.customer.wishlists[0]?.productVariants?.slice(0, 2) || null;
  },
};

const actions = {
  checkAccessToken({ commit, dispatch }) {
    const itemStr = localStorage.getItem("shopifyCustomerAccessToken");

    if (!itemStr) {
      return null;
    }

    const item = JSON.parse(itemStr);
    const now = new Date();

    if (now.getTime() > item.expiry) {
      dispatch("attemptLogOut");
      return null;
    }

    commit("setCustomerAccessToken", item);
  },
  fetchCustomer({ commit, dispatch }) {
    const itemStr = localStorage.getItem("shopifyCustomer");

    if (!itemStr) {
      return null;
    }

    const json = JSON.parse(itemStr);

    commit("setCustomer", json);
    dispatch("multipassAccount", null, { root: true });
  },
  async attemptLogin({ dispatch }, { email, password, returnUrl }) {
    const { data } = await apollo.mutate({
      mutation: createCustomerAccessTokenMutation,
      variables: {
        input: {
          email,
          password,
        },
      },
    });

    const token = data.customerAccessTokenCreate.customerAccessToken;
    if (token) {
      await dispatch("setCustomerAccessToken", token);
      await dispatch("getCustomerDetails");
      await dispatch("multipassAccount", returnUrl, { root: true });
      dispatch("multipassCheckoutUrl", null, { root: true });
      dispatch("getCustomerDetails");

      await dispatch("populateUserQuestions", { email });
      loginEvent({ method: "Form" });
    }

    return data.customerAccessTokenCreate;
  },
  async getCustomerDetails({ dispatch, state }) {
    if (!state.customerAccessToken?.value) {
      return;
    }

    const { data } = await apollo.query({
      query: customerQuery,
      variables: {
        accessToken: state.customerAccessToken.value,
      },
    });

    const { customer } = data;

    if (!customer) {
      Sentry.captureMessage("Customer doesn't exist", "error");
      return false;
    }

    const modifiedCustomer = {
      ...customer,
      ...(customer.loyalty?.value
        ? { loyalty: { ...JSON.parse(customer.loyalty.value) } }
        : {}),
      ...(customer.wishlists?.value
        ? { wishlists: JSON.parse(customer.wishlists.value) }
        : {}),
    };

    dispatch("setCustomer", modifiedCustomer);
    dispatch("checkWishlist");
  },
  async checkWishlist({ state }) {
    let customer = state.customer;

    console.log(customer, "CUSTOMER");

    if (customer.wishlists) {
      return localStorage.setItem(
        "ratandboaWishlist",
        JSON.stringify(customer.wishlists)
      );
    }
  },
  async attemptSSOLogin({ dispatch }, { accessToken, returnUrl }) {
    dispatch("setCustomerAccessToken", { accessToken });

    const { data } = await apollo.query({
      query: customerQuery,
      variables: {
        accessToken,
      },
    });

    if (!data.customer) {
      Sentry.captureMessage("SSO customer doesn't exist", "error");
      return false;
    }

    const email = data.customer.email;

    const newUserUrl = new URL(
      process.env.RATANDBOA_NODE_APP_URL + "/check-user-exists"
    );
    newUserUrl.searchParams.append("email", email);

    const newUser = await fetch(newUserUrl);
    const newUserJson = await newUser.json();
    const preExists = newUserJson.exists;

    if (!preExists) {
      await registerEvent({ method: "Google SSO", email });
      dispatch("updateEmailMarketingConsent");
    } else {
      loginEvent({ method: "Google SSO" });
    }

    dispatch("setCustomer", data.customer);
    dispatch("multipassCheckoutUrl", null, { root: true });

    await dispatch("multipassAccount", returnUrl, { root: true });

    return true;
  },
  async attemptRegister(
    { dispatch },
    { firstName, lastName, email, password, phone, returnUrl }
  ) {
    const { data } = await apollo.mutate({
      mutation: createUserMutation,
      variables: {
        input: {
          firstName,
          lastName,
          email,
          password,
          phone,
          acceptsMarketing: true,
        },
      },
    });

    if (data.customerCreate.customerUserErrors.length >= 1) {
      return data.customerCreate;
    }

    await dispatch("populateUserQuestions", { email });
    await registerEvent({ method: "Form", email });

    return await dispatch("attemptLogin", { email, password, returnUrl });
  },
  async populateUserQuestions(_, { email }) {
    const preRegisterQuestions = localStorage.getItem("preRegisterQuestions");
    if (preRegisterQuestions) {
      await fetch(process.env.RATANDBOA_NODE_APP_URL + "/questionnaire", {
        method: "post",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          questions: JSON.parse(preRegisterQuestions),
        }),
      });

      localStorage.removeItem("preRegisterQuestions");
    }
  },
  attemptLogOut({ commit }) {
    localStorage.removeItem("shopifyCustomer");
    commit("setCustomer", {
      id: null,
      email: null,
      birthday: null,
    });
    localStorage.removeItem("shopifyCustomerAccessToken");
    commit("setCustomerAccessToken", null);
  },
  setCustomerAccessToken({ commit }, { accessToken, expiresAt }) {
    const sevenDayExpiry = add(new Date(), { days: 7 });

    const expiry = expiresAt
      ? new Date(expiresAt).getTime().toString()
      : sevenDayExpiry.getTime().toString();

    const item = {
      value: accessToken,
      expiry,
    };

    localStorage.setItem("shopifyCustomerAccessToken", JSON.stringify(item));

    commit("setCustomerAccessToken", item);
  },
  setCustomer({ commit }, customer) {
    const string = JSON.stringify(customer);
    localStorage.setItem("shopifyCustomer", string);
    commit("setCustomer", customer);
  },
  multipassAccount: {
    root: true,
    async handler({ commit, state, dispatch }, redirectUrl) {
      const email = state.customer?.email;

      if (!email) {
        return false;
      }

      const input = {
        email,
        redirectUrl: redirectUrl || "/account",
      };

      Sentry.addBreadcrumb({
        category: "auth",
        message: "multipassAccount input",
        data: input,
        level: "info",
      });

      const multipassInputString = new URLSearchParams(input).toString();
      const multipassResponse = await fetch(
        process.env.RATANDBOA_NODE_APP_URL +
          "/multipass?" +
          multipassInputString
      );

      Sentry.addBreadcrumb({
        category: "auth",
        message: "multipassAccount response",
        data: multipassResponse,
        level: "info",
      });

      const multipassData = await multipassResponse.json();

      Sentry.addBreadcrumb({
        category: "auth",
        message: "multipassAccount json",
        data: multipassData,
        level: "info",
      });

      if (isEmpty(multipassData)) {
        Sentry.captureMessage("Empty multipass json", "error");
        return;
      }

      if (!multipassData.token) {
        Sentry.captureMessage("No multipass token", "error");
        return;
      }

      commit("setMultipassAccount", multipassData);

      const { data } = await apollo.mutate({
        mutation: customerAccessTokenCreateWithMultipass,
        variables: {
          multipassToken: multipassData.token,
        },
      });

      const token =
        data.customerAccessTokenCreateWithMultipass.customerAccessToken;

      const errors =
        data.customerAccessTokenCreateWithMultipass.customerUserErrors;
      if (errors.length >= 1) {
        Sentry.captureMessage(errors);
        errors.forEach((error) => {
          throw new Error(error.code);
        });
      }

      if (token) {
        dispatch("setCustomerAccessToken", token);
      }
    },
  },
  async updateEmailMarketingConsent({ state }) {
    const input = {
      id: state.customer.id,
      emailConsented: true,
    };

    await fetch(process.env.RATANDBOA_NODE_APP_URL + "/customer/account", {
      method: "post",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
  },
  async attemptCustomerUpdate(_, updateInput) {
    const resp = await fetch(
      process.env.RATANDBOA_NODE_APP_URL + "/customer/account",
      {
        method: "post",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updateInput),
      }
    );

    console.log(resp);

    return resp;
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
